import React, { useState, useEffect } from 'react';
import { api } from './utils/api';

// Hardcoded fallback — used if API is unreachable
const FALLBACK_PROJECTS = [
  {
    name: 'PENN RELAYS', client: 'ON RUNNING', slug: 'penn-relays',
    images: ['/images/pennrelays/PBT_20230428-063230_TeddyBayer_PT101242.jpg'],
  },
  {
    name: 'KARISSA SCHWEIZER', client: 'PERSONAL', slug: 'karissa-schweizer',
    images: ['/images/karissa/PBT_20220716-144620_KarissaSchweizer__DSC7435-2.jpg'],
  },
  {
    name: 'BROOKS XC', client: '[3-21]', slug: 'brooks-xc',
    images: [
      '/images/brooksxc/PBT_20251213-121722_BrooksXC_DSC07796.jpg',
      '/images/brooksxc/PBT_20251213-121737_BrooksXC_DSC07808.jpg',
      '/images/brooksxc/PBT_20251213-122022_BrooksXC_DSC07901.jpg',
      '/images/brooksxc/PBT_20251213-122231_BrooksXC_DSC07927.jpg',
      '/images/brooksxc/PBT_20251213-122343_BrooksXC_DSC07975.jpg',
      '/images/brooksxc/PBT_20251213-122544_BrooksXC_DSC07996.jpg',
      '/images/brooksxc/PBT_20251213-123709_BrooksXC_DSC08074.jpg',
      '/images/brooksxc/PBT_20251213-124151_BrooksXC_DSC08184.jpg',
      '/images/brooksxc/PBT_20251213-124229_BrooksXC_DSC08189.jpg',
      '/images/brooksxc/PBT_20251213-124236_BrooksXC_DSC08201.jpg',
      '/images/brooksxc/PBT_20251213-124705_BrooksXC_DSC08252.jpg',
      '/images/brooksxc/PBT_20251213-124844_BrooksXC_PT107378.jpg',
      '/images/brooksxc/PBT_20251213-125156_BrooksXC_DSC08281.jpg',
      '/images/brooksxc/PBT_20251213-131152_BrooksXC_DSC08511.jpg',
      '/images/brooksxc/PBT_20251213-131248_BrooksXC_DSC08542.jpg',
      '/images/brooksxc/PBT_20251213-131605_BrooksXC_DSC08569.jpg',
      '/images/brooksxc/PBT_20251213-132711_BrooksXC_DSC08757.jpg',
      '/images/brooksxc/PBT_20251213-132725_BrooksXC_DSC08768.jpg',
      '/images/brooksxc/PBT_20251213-143634_BrooksXC_PT107528.jpg',
      '/images/brooksxc/PBT_20251213-143407_BrooksXC_PT107475.jpg',
      '/images/brooksxc/PBT_20251213-143929_BrooksXC_PT107596.jpg',
    ],
  },
];

const App = () => {
  const [currentPage, setCurrentPage] = useState('work');
  const [currentView, setCurrentView] = useState('single');
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hoveredProjectIndex, setHoveredProjectIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [allProjects, setAllProjects] = useState(FALLBACK_PROJECTS);
  const [showTransition, setShowTransition] = useState(false);
  const [imageOrientations, setImageOrientations] = useState({});
  const [projectImageIndices, setProjectImageIndices] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const pendingSlugRef = React.useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      
      const isMobileDevice = hasCoarsePointer || (isTouchDevice && !hasFinePointer) || isSmallScreen;
      
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (currentView !== 'index') setProjectImageIndices({});
  }, [currentView]);

  // Preload all secondary images when entering index so hover doesn't flicker
  useEffect(() => {
    if (currentView !== 'index') return;
    allProjects.forEach(p => {
      p.images.slice(1).forEach(src => {
        const img = new Image();
        img.src = src;
      });
    });
  }, [currentView, allProjects]);


  useEffect(() => {
    const themeColor = '#f2f2ee';
    
    let metaThemeColor = document.querySelector("meta[name='theme-color']");
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = themeColor;

    let metaAppleStatusBar = document.querySelector("meta[name='apple-mobile-web-app-status-bar-style']");
    if (!metaAppleStatusBar) {
      metaAppleStatusBar = document.createElement('meta');
      metaAppleStatusBar.name = 'apple-mobile-web-app-status-bar-style';
      document.head.appendChild(metaAppleStatusBar);
    }
    metaAppleStatusBar.content = currentPage === 'contact' ? 'black-translucent' : 'default';
  }, [currentPage]);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/contact') {
      setCurrentPage('contact');
    } else if (path === '/index') {
      setCurrentPage('work');
      setCurrentView('index');
    } else if (path.startsWith('/project/')) {
      const slug = path.replace('/project/', '').replace(/\/$/, '');
      setCurrentPage('work');
      setCurrentView('single');
      const idx = FALLBACK_PROJECTS.findIndex(p => p.slug === slug);
      if (idx !== -1) setCurrentProjectIndex(idx);
      else pendingSlugRef.current = slug;
    } else {
      setCurrentPage('work');
      setCurrentView('single');
    }
  }, []);

  useEffect(() => {
    const slug = allProjects[currentProjectIndex]?.slug;
    if (currentPage === 'contact') {
      window.history.replaceState({}, '', '/contact');
    } else if (currentView === 'index') {
      window.history.replaceState({}, '', '/index');
    } else if (currentView === 'single' && slug) {
      window.history.replaceState({}, '', `/project/${slug}`);
    } else {
      window.history.replaceState({}, '', '/');
    }
  }, [currentPage, currentView, currentProjectIndex, allProjects]);

  useEffect(() => {
    api.projects.list()
      .then(data => {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8787';
        const shaped = data.map(p => ({
          ...p,
          cover_focal_point: p.images[0]?.focal_point || 'center center',
          images: p.images.map(img =>
            img.src.startsWith('/uploads/') ? `${apiBase}${img.src}` : img.src
          ),
        }));
        if (shaped.length > 0) {
          setAllProjects(shaped);
          if (pendingSlugRef.current) {
            const idx = shaped.findIndex(p => p.slug === pendingSlugRef.current);
            if (idx !== -1) setCurrentProjectIndex(idx);
            pendingSlugRef.current = null;
          }
        }
      })
      .catch(() => {});
  }, []);

  const currentProject = allProjects[currentProjectIndex];
  const totalImagesInProject = currentProject.images.length;

  useEffect(() => {
    const imagesToPreload = [];
    if (currentImageIndex < totalImagesInProject - 1) {
      imagesToPreload.push(currentProject.images[currentImageIndex + 1]);
    }
    if (currentImageIndex > 0) {
      imagesToPreload.push(currentProject.images[currentImageIndex - 1]);
    }
    if (currentImageIndex === totalImagesInProject - 1 && currentProjectIndex < allProjects.length - 1) {
      imagesToPreload.push(allProjects[currentProjectIndex + 1].images[0]);
    }
    if (currentImageIndex === 0 && currentProjectIndex > 0) {
      const prevProject = allProjects[currentProjectIndex - 1];
      imagesToPreload.push(prevProject.images[prevProject.images.length - 1]);
    }
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [currentImageIndex, currentProjectIndex, currentProject.images, totalImagesInProject, allProjects]);

  useEffect(() => {
    if (showTransition) {
      const timer = setTimeout(() => {
        setShowTransition(false);
      }, 1750);
      return () => clearTimeout(timer);
    }
  }, [showTransition]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentPage === 'work' && currentView === 'single') {
        if (e.key === 'ArrowRight') {
          handleNext();
        } else if (e.key === 'ArrowLeft') {
          handlePrev();
        }
      }
      if (currentPage === 'work' && currentView === 'overview') {
        if (e.key === 'ArrowDown') {
          triggerProjectTransition();
        } else if (e.key === 'ArrowUp') {
          triggerPreviousProjectTransition();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, currentView, currentImageIndex, totalImagesInProject, currentProjectIndex, allProjects.length]);

  useEffect(() => {
    if (currentPage !== 'work' || currentView !== 'overview') return;
    
    const handleWheel = (e) => {
      if (e.deltaY > 0) {
        triggerProjectTransition();
      } else if (e.deltaY < 0) {
        triggerPreviousProjectTransition();
      }
    };
    
    window.addEventListener('wheel', handleWheel);
    return () => window.removeEventListener('wheel', handleWheel);
  }, [currentPage, currentView]);

  const triggerProjectTransition = () => {
    const nextIdx = currentProjectIndex < allProjects.length - 1 ? currentProjectIndex + 1 : 0;
    setIsTransitioning(true);
    setShowTransition(true);
    setCurrentProjectIndex(nextIdx);
    setCurrentImageIndex(0);
    setCurrentView('single');
    setTimeout(() => setIsTransitioning(false), 1750);
  };

  const triggerPreviousProjectTransition = () => {
    const prevIdx = currentProjectIndex > 0 ? currentProjectIndex - 1 : allProjects.length - 1;
    setIsTransitioning(true);
    setShowTransition(true);
    setCurrentProjectIndex(prevIdx);
    setCurrentImageIndex(0);
    setCurrentView('single');
    setTimeout(() => setIsTransitioning(false), 1750);
  };

  const handleNext = () => {
    if (currentImageIndex < totalImagesInProject - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else {
      triggerProjectTransition();
    }
  };

  const handlePrev = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    } else {
      const prevIdx = currentProjectIndex > 0 ? currentProjectIndex - 1 : allProjects.length - 1;
      const prevProject = allProjects[prevIdx];
      setShowTransition(true);
      setCurrentProjectIndex(prevIdx);
      setCurrentImageIndex(prevProject.images.length - 1);
      setCurrentView('single');
    }
  };

  const handleImageClick = (e) => {
    if (currentView !== 'single') return;
    
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    
    if (clickX > screenWidth / 2) {
      handleNext();
    } else {
      handlePrev();
    }
  };

  const minSwipeDistance = 50;
  const touchStartPos = React.useRef({ x: 0, y: 0 });

  const handleTouchStart = (e) => {
    touchStartPos.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const distanceX = touchStartPos.current.x - touchEndX;
    const distanceY = Math.abs(touchStartPos.current.y - touchEndY);
    const screenWidth = window.innerWidth;
    
    if (distanceY < 50) {
      if (Math.abs(distanceX) < 10) {
        const tapX = touchEndX;
        if (tapX > screenWidth / 2) {
          handleNext();
        } else {
          handlePrev();
        }
      }
      else if (distanceX > minSwipeDistance) {
        handleNext();
      } else if (distanceX < -minSwipeDistance) {
        handlePrev();
      }
    }
    
    touchStartPos.current = { x: 0, y: 0 };
  };

  const handleTouchMove = (e) => {
  };

  const handleImageLoad = (e, projectIndex, imageIndex) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const key = `${projectIndex}-${imageIndex}`;
    
    setImageOrientations(prev => ({
      ...prev,
      [key]: aspectRatio > 1.0 ? 'horizontal' : 'vertical'
    }));
  };

  const handleProjectHover = (index, project) => {
    if (isMobile || project.images.length <= 1) return;
    setProjectImageIndices(prev => ({
      ...prev,
      [index]: ((prev[index] ?? 0) + 1) % project.images.length,
    }));
  };

  const WorkPage = () => {
    const currentImage = currentProject.images[currentImageIndex];
    const nextImageIndex = currentImageIndex < totalImagesInProject - 1 ? currentImageIndex + 1 : 0;
    const nextImage = currentProject.images[nextImageIndex];

    if (currentView === 'overview') {
      return (
        <div 
          key="overview"
          className={`fixed inset-0 overflow-y-auto ${showTransition ? 'animate-fadeInGrid' : ''}`}
          style={{
            zIndex: 45,
            backgroundColor: '#f2f2ee',
            paddingTop: isMobile ? '32px' : '28px',
            paddingBottom: '28px'
          }}
        >
          <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
            <div className="grid grid-cols-5 md:grid-cols-30 gap-2">
              {currentProject.images.map((img, index) => (
                <div
                  key={index}
                  className={`cursor-pointer group ${index !== currentImageIndex ? 'animate-fadeInGrid' : ''}`}
                  onClick={() => { setShowTransition(false); setCurrentImageIndex(index); setCurrentView('single'); }}
                  style={{ aspectRatio: '4/5', overflow: 'hidden' }}
                >
                  <img
                    src={img}
                    alt={`${currentProject.name} ${index + 1}`}
                    className="w-full h-full object-cover transition-opacity duration-300 ease-out group-hover:opacity-70"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'index') {
      return (
        <div key="index" className="fixed inset-0 overflow-y-auto" style={{
          zIndex: 45,
          backgroundColor: '#f2f2ee',
          paddingTop: isMobile ? '44px' : '28px',
          paddingBottom: '28px'
        }}>
          <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-y-3 md:gap-y-2 gap-x-2">
              {allProjects.map((project, index) => {
                if (!project.images || !project.images[0]) return null;

                const imgIndex = isMobile ? 0 : (projectImageIndices[index] || 0);
                const img = project.images[imgIndex];
                const imgKey = `${index}-${imgIndex}`;
                const horizontal = imageOrientations[imgKey] === 'horizontal';

                const handleNavigation = () => {
                  setCurrentProjectIndex(index);
                  setCurrentImageIndex(0);
                  setCurrentView('single');
                };

                const hasHover = window.matchMedia('(hover: hover)').matches;
                const shouldEnableHover = hasHover && !isMobile && project.images.length > 1;

                return (
                  <div
                    key={index}
                    style={{ cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}
                    onMouseDown={handleNavigation}
                    onMouseEnter={() => { setHoveredProjectIndex(index); if (shouldEnableHover) handleProjectHover(index, project); }}
                    onMouseLeave={() => setHoveredProjectIndex(null)}>
                    <div style={{
                      overflow: 'hidden',
                      position: 'relative',
                      aspectRatio: '4/5',
                      backgroundColor: '#f2f2ee'
                    }}>
                      <img
                        src={img}
                        alt={project.name}
                        loading="lazy"
                        decoding="async"
                        onLoad={(e) => handleImageLoad(e, index, imgIndex)}
                        draggable={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: horizontal ? 'contain' : 'cover',
                          objectPosition: project.cover_focal_point || 'center center',
                          transition: isMobile ? 'none' : 'all 0.5s ease-out',
                          pointerEvents: 'none'
                        }}
                      />
                      {!isMobile && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'rgba(242,242,238,0.88)',
                          opacity: hoveredProjectIndex === index ? 1 : 0,
                          transition: 'opacity 0.25s ease',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', gap: 6,
                          pointerEvents: 'none',
                        }}>
                          <p style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{project.name}</p>
                          <p style={{ color: '#8b7d6b', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.04em' }}>{project.client}</p>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: '5px', pointerEvents: 'none' }}>
                      <p className="uppercase" style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.04em', lineHeight: '1.2' }}>{project.name}</p>
                      <p style={{ color: '#8b7d6b', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.04em', lineHeight: '1.2' }}>{project.client}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        key="single" 
        className={`fixed inset-0 ${showTransition ? 'animate-fadeInGrid' : ''}`}
        style={{
          zIndex: 45,
          backgroundColor: '#f2f2ee',
          paddingTop: isMobile ? '32px' : '28px',
          paddingBottom: '28px'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="h-full flex items-start"
          style={{ paddingLeft: '10px', paddingRight: '10px' }}
          onClick={handleImageClick}
        >
          <div className="blur-load loaded" style={isMobile ? { width: '100%' } : {}}>
            <img
              key={`${currentProjectIndex}-${currentImageIndex}`}
              src={currentImage}
              alt={currentProject.name}
              className={isMobile ? 'w-full h-auto object-contain select-none' : 'w-auto h-[85vh] object-contain select-none'}
              draggable="false"
              onLoad={(e) => e.target.parentElement.classList.add('loaded')}
            />
          </div>
        </div>
        
        {totalImagesInProject > 1 && !isMobile && (
          <div 
            className="fixed bottom-14 right-3 cursor-pointer group"
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            style={{ zIndex: 51 }}
          >
            <div className="h-10 w-auto flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#f2f2ee' }}>
              <img 
                src={nextImage} 
                alt="Next"
                className="h-full w-auto object-cover opacity-50 transition-opacity duration-300 group-hover:opacity-70"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const ContactPage = () => {
    const [accoladesOpen, setAccoladesOpen] = useState(false);
    const sz = 'clamp(9px, 0.9vw, 13px)';
    const muted = '#8b7d6b';
    const hover = (e) => e.target.style.color = muted;
    const unhover = (e) => e.target.style.color = '#0A0A0A';
    const label = { fontSize: sz, letterSpacing: '0.06em', color: muted, textTransform: 'uppercase', marginBottom: 6, display: 'block' };
    const block = { fontSize: sz, lineHeight: 1.6, letterSpacing: '0.02em' };

    const clientsList = (
      <>
        <p>On, Nike, PUMA, HOKA, New Balance, Under Armour, Brooks Running, Salomon, ASICS</p>
        <p>ROKA, VEJA, KANE Footwear, Teva, rabbit, Satisfy Running, Maurten, WHOOP</p>
        <p>Athletic Greens, Garmin, Gatorade, Runner's World, Well+Good, FloSports, Momentum</p>
        <p>USA Triathlon, OBE, J.P. Morgan, Barclays, Gap Inc., Amazon Ads, Wyndham</p>
      </>
    );

    const accoladesList = (
      <>
        <p>2023 — Sports Photographer of the Year; Trophy Journal</p>
        <p>2022 — Photo of the Year; Portland Track</p>
      </>
    );

    if (isMobile) {
      return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: '#f2f2ee', overflowY: 'auto', paddingTop: 44, paddingBottom: 28, paddingLeft: 10, paddingRight: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

            <div>
              <span style={label}>Identity</span>
              <div style={block}>
                <p>PIERCE BANCROFT TOWNSEND</p>
                <p style={{ color: muted }}>AUSTIN, TEXAS</p>
              </div>
            </div>

            <div>
              <span style={label}>Contact</span>
              <div style={block}>
                <p><a href="https://instagram.com/piercebtownsend" target="_blank" rel="noopener noreferrer" onMouseEnter={hover} onMouseLeave={unhover}>@piercebtownsend</a></p>
                <p><a href="mailto:hello@piercebtownsend.com" onMouseEnter={hover} onMouseLeave={unhover}>hello@piercebtownsend.com</a></p>
                <p><a href="tel:+15127748261" onMouseEnter={hover} onMouseLeave={unhover}>+1 512 774 8261</a></p>
              </div>
            </div>

            <div>
              <span style={label}>Select Clients</span>
              <div style={block}>{clientsList}</div>
            </div>

            <div>
              <button
                onClick={() => setAccoladesOpen(o => !o)}
                style={{ ...label, cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                ACCOLADES
                <span style={{ fontSize: sz, color: muted }}>{accoladesOpen ? '−' : '+'}</span>
              </button>
              {accoladesOpen && <div style={block}>{accoladesList}</div>}
            </div>

          </div>
        </div>
      );
    }

    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: '#f2f2ee', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '28px 10px' }}>

        {/* Left — identity + contact */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <span style={label}>Identity</span>
            <div style={block}>
              <p>PIERCE BANCROFT TOWNSEND</p>
              <p style={{ color: muted }}>AUSTIN, TEXAS</p>
            </div>
          </div>
          <div>
            <span style={label}>Contact</span>
            <div style={block}>
              <p><a href="https://instagram.com/piercebtownsend" target="_blank" rel="noopener noreferrer" onMouseEnter={hover} onMouseLeave={unhover}>@piercebtownsend</a></p>
              <p><a href="mailto:hello@piercebtownsend.com" onMouseEnter={hover} onMouseLeave={unhover}>hello@piercebtownsend.com</a></p>
              <p><a href="tel:+15127748261" onMouseEnter={hover} onMouseLeave={unhover}>+1 512 774 8261</a></p>
            </div>
          </div>
        </div>

        {/* Right — clients + accolades */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'right' }}>
          <div>
            <span style={{ ...label, display: 'block', textAlign: 'right' }}>Select Clients</span>
            <div style={block}>{clientsList}</div>
          </div>
          <div>
            <button
              onClick={() => setAccoladesOpen(o => !o)}
              style={{ ...label, cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, width: '100%' }}>
              ACCOLADES
              <span style={{ fontSize: sz, color: muted }}>{accoladesOpen ? '−' : '+'}</span>
            </button>
            {accoladesOpen && <div style={{ ...block, textAlign: 'right' }}>{accoladesList}</div>}
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f2f2ee' }}>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
        style={{ backgroundColor: '#f2f2ee' }}>
        <div className="flex items-center justify-between" style={{ padding: '6px 10px' }}>
          <button
            onClick={() => { setHoveredProjectIndex(null); setCurrentPage('work'); setCurrentView('single'); setCurrentProjectIndex(0); setCurrentImageIndex(0); }}
            className="hover:opacity-60 transition-opacity uppercase"
            style={{
              color: '#0A0A0A',
              fontSize: 'clamp(9px, 0.9vw, 13px)',
              letterSpacing: '0.05em',
            }}>
            PIERCE BANCROFT TOWNSEND
          </button>
          <div className="flex items-center" style={{ gap: '20px' }}>
            <button
              onClick={() => {
                if (currentPage === 'contact') setCurrentPage('work');
                setHoveredProjectIndex(null);
                setProjectImageIndices({});
                setCurrentView('index');
              }}
              className="hover:opacity-60 transition-opacity uppercase"
              style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}>
              {currentPage === 'work' && currentView === 'index' && (
                <span className="inline-block rounded-full align-middle"
                  style={{ width: 5, height: 5, backgroundColor: '#0A0A0A', marginBottom: '0.15em', marginRight: 4, display: 'inline-block' }}></span>
              )}
              INDEX
            </button>
            <button
              onClick={() => { setHoveredProjectIndex(null); setCurrentPage('contact'); }}
              className="hover:opacity-60 transition-opacity uppercase"
              style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}>
              {currentPage === 'contact' && (
                <span className="inline-block rounded-full align-middle"
                  style={{ width: 5, height: 5, backgroundColor: '#0A0A0A', marginBottom: '0.15em', marginRight: 4, display: 'inline-block' }}></span>
              )}
              CONTACT
            </button>
          </div>
        </div>
      </nav>
      {currentPage === 'work' && currentView === 'single' && (
        <div className={`fixed bottom-0 left-0 right-0 z-50 ${showTransition ? 'animate-fadeInGrid' : ''}`} style={{ backgroundColor: '#f2f2ee' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px' }}>
            <div className="flex items-center" style={{ gap: '16px' }}>
              <button onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                className="transition-colors uppercase"
                style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}
                onMouseEnter={(e) => e.target.style.color = '#8b7d6b'}
                onMouseLeave={(e) => e.target.style.color = '#0A0A0A'}>← PREV</button>
              <button onClick={(e) => { e.stopPropagation(); handleNext(); }}
                className="transition-colors uppercase"
                style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}
                onMouseEnter={(e) => e.target.style.color = '#8b7d6b'}
                onMouseLeave={(e) => e.target.style.color = '#0A0A0A'}>NEXT →</button>
            </div>
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <p className="uppercase whitespace-nowrap" style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}>
                <span>{String(currentImageIndex + 1).padStart(2, '0')}/{String(totalImagesInProject).padStart(2, '0')}</span>
                <span className="hidden md:inline"> — {currentProject.name}</span>
              </p>
            </div>
            <button onClick={(e) => {
              e.stopPropagation();
              setHoveredProjectIndex(null);
              setShowTransition(false);
              setCurrentView(currentView === 'overview' ? 'single' : 'overview');
            }}
              className="transition-colors uppercase"
              style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}
              onMouseEnter={(e) => e.target.style.color = '#8b7d6b'}
              onMouseLeave={(e) => e.target.style.color = '#0A0A0A'}>
              {currentView === 'overview' ? 'OVERVIEW [-]' : 'OVERVIEW [+]'}
            </button>
          </div>
        </div>
      )}
      {currentPage === 'work' && currentView === 'overview' && (
        <div className={`fixed bottom-0 left-0 right-0 z-50 ${showTransition ? 'animate-fadeInGrid' : ''}`} style={{ backgroundColor: '#f2f2ee' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px' }}>
            <div>
              <p className="uppercase whitespace-nowrap md:hidden" style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}>
                {currentProject.name}
              </p>
            </div>
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
              <p className="uppercase whitespace-nowrap" style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}>
                {currentProject.name}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); triggerProjectTransition(); }}
              className="transition-colors uppercase"
              style={{ color: '#0A0A0A', fontSize: 'clamp(9px, 0.9vw, 13px)', letterSpacing: '0.05em' }}
              onMouseEnter={(e) => e.target.style.color = '#8b7d6b'}
              onMouseLeave={(e) => e.target.style.color = '#0A0A0A'}>
              NEXT PROJECT →
            </button>
          </div>
        </div>
      )}
      <div>
        {currentPage === 'work' && <WorkPage />}
        {currentPage === 'contact' && <ContactPage />}
      </div>
      
      {isTransitioning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '2px',
          backgroundColor: 'transparent',
          zIndex: 9999,
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: '100%',
            backgroundColor: '#8b7d6b',
            transformOrigin: 'left',
            animation: 'progressBar 1.75s ease-out forwards'
          }} />
        </div>
      )}
      
      <style>{`
        * { 
          -webkit-font-smoothing: antialiased; 
          -moz-osx-font-smoothing: grayscale;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html { scroll-behavior: smooth; }
        body { background: #f2f2ee; }
        @keyframes fadeInGrid { from { opacity: 0; } to { opacity: 1; } }
        .animate-fadeInGrid { animation: fadeInGrid 1.75s ease-out; }
        @media (min-width: 768px) {
          .md\\:grid-cols-30 { grid-template-columns: repeat(30, minmax(0, 1fr)); }
        }
        
        a, button, nav, .transition-colors {
          transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
        }
        
        button {
          background: none;
          border: none;
          font-family: inherit;
          cursor: pointer;
        }
        
        .blur-load {
          background-size: cover;
          background-position: center;
        }
        .blur-load img {
          opacity: 0;
          transition: opacity 0.5s ease-in-out;
        }
        .blur-load.loaded img {
          opacity: 1;
        }
        
        @keyframes progressBar {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
      `}</style>
      <link rel="icon" type="image/svg+xml" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23000000'/%3E%3C/svg%3E" />
    </div>
  );
};

export default App;